// src/pages/vs/VsTraditionalDubbingPage.js
// Cinematic monochrome redesign — mirrors VsDeepDubPage / VsHeyGenPage structure.
// Traditional dubbing = studio workflow (voice actors, adapters, engineers).
// We concede prestige M&E to studios and win on speed, cost, scale, and iteration.
import React, { useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import useSEO from '../../hooks/useSEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const BREADCRUMBS = [
  { name: 'Compare', url: 'https://dublabs.ai/vs/traditional-dubbing' },
  { name: 'vs Traditional Dubbing', url: 'https://dublabs.ai/vs/traditional-dubbing' },
];

// ─── Data ─────────────────────────────────────────────────────────────────
const COMPARISON_ROWS = [
  {
    label: 'Turnaround per language',
    dublabs:    { text: 'Minutes — a short video is done before lunch', win: 'us' },
    competitor: { text: '4–8 weeks: casting, recording, adaptation, QC', win: 'them' },
  },
  {
    label: 'Cost per finished minute',
    dublabs:    { text: 'Roughly 90% less — no talent or studio overhead', win: 'us' },
    competitor: { text: '$50–$200+ per finished minute per language', win: 'them' },
  },
  {
    label: 'Language scale',
    dublabs:    { text: 'All target languages processed in parallel', win: 'us' },
    competitor: { text: 'Serial — one studio session, one language at a time', win: 'them' },
  },
  {
    label: 'Voice preservation',
    dublabs:    { text: 'Clones the original speaker across every language', win: 'us' },
    competitor: { text: 'Recasts each language with a different voice actor', win: 'them' },
  },
  {
    label: 'Iteration speed',
    dublabs:    { text: 'Script change? Re-dub in minutes, same voice', win: 'us' },
    competitor: { text: 'Book the studio, re-cast, re-record — days to weeks', win: 'them' },
  },
  {
    label: 'Consistency across episodes',
    dublabs:    { text: 'Same voice model episode after episode, forever', win: 'us' },
    competitor: { text: 'Actor availability drives casting drift over time', win: 'them' },
  },
  {
    label: 'Access model',
    dublabs:    { text: 'Contact us → free sample dub in days', win: 'us' },
    competitor: { text: 'RFP, quotes, retainers, PO cycles', win: 'them' },
  },
  {
    label: 'Emotional nuance',
    dublabs:    { text: 'AI-preserved from source; strong on informational & narrative', win: 'tie' },
    competitor: { text: 'Human actor adaptation; still the ceiling for dramatic performance', win: 'tie' },
  },
  {
    label: 'Feature-length cinematic dubs',
    dublabs:    { text: 'Excellent for streaming, edtech, corporate — not our focus for film', win: 'them' },
    competitor: { text: 'Still the gold standard for prestige feature-film localization', win: 'them' },
  },
  {
    label: 'Best for',
    dublabs:    { text: 'Media, edtech, corporate, marketing — anything shipped weekly', win: 'us' },
    competitor: { text: 'Prestige film, high-budget episodic M&E', win: 'them' },
  },
];

const ADVANTAGES = [
  {
    title: 'Weeks collapse into minutes',
    body: 'A traditional studio takes 4–8 weeks per language: script adaptation, casting, booking, recording, mixing, QC. DubLabs delivers the same output in minutes — because there\'s no calendar to reconcile, no talent to book, no room to reserve. Ship the video the same day you finalize the master.',
  },
  {
    title: 'Roughly 90% cost reduction',
    body: 'Studio dubbing runs $50–$200+ per finished minute per language once you add adapters, voice actors, engineers, and studio time. DubLabs is per-video usage — same output, at a fraction of the cost, which is what makes 5–10 language rollouts economical for teams that aren\'t Netflix.',
  },
  {
    title: 'Every language, in parallel',
    body: 'A studio dubs one language at a time — that\'s how the calendar works. DubLabs processes all target languages simultaneously. A 3-minute clip into 5 languages is done in the time a studio spends booking the first session.',
  },
  {
    title: 'Your speaker\'s voice, in every language',
    body: 'Studios cast a different voice actor per language. Your CEO, your teacher, your on-screen creator becomes a different person in each dub. DubLabs clones the original speaker so the audience hears the same voice — a continuity you can\'t buy at any studio, at any price.',
  },
];

const USE_CASES = [
  {
    title: 'Choose DubLabs when:',
    items: [
      'You ship weekly and can\'t wait 6–8 weeks per language',
      'Budgets don\'t support studio economics (talent + studio + QC)',
      'You want the original speaker\'s voice preserved across languages',
      'You need to iterate — re-cut, re-script, re-release quickly',
      'You\'re localizing 3+ languages and doing it serially is the bottleneck',
    ],
    highlight: true,
  },
  {
    title: 'Choose a traditional studio when:',
    items: [
      'You\'re dubbing prestige feature film or premium episodic drama',
      'Six-figure per-title budget is normal',
      'A human actor\'s dramatic performance is the deliverable',
      'The project ships once and never iterates',
      'Awards-cycle prestige matters more than turnaround',
    ],
    highlight: false,
  },
];

// ─── Reusable helpers ─────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 780, mx: 'auto', px: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {eyebrow && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', mb: 2.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{
          fontFamily: 'Inter', fontWeight: 900,
          fontSize: 'clamp(1.9rem, 4.5vw, 3.6rem)',
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em', mb: subtitle ? 2 : 0,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 300,
            fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
            color: 'rgba(255,255,255,0.45)', lineHeight: 1.6,
            maxWidth: 600, mx: 'auto',
          }}>
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
}

function WinIcon({ result, ours }) {
  if (result === 'tie') {
    return <RemoveIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 18 }} />;
  }
  if ((result === 'us' && ours) || (result === 'them' && !ours)) {
    return <CheckIcon sx={{ color: '#fff', fontSize: ours ? 22 : 18, opacity: ours ? 1 : 0.55 }} />;
  }
  return <CloseIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 16 }} />;
}

function ComparisonTable({ rows, competitorName }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.15, once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box sx={{
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(16px)',
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.62rem',
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', alignSelf: 'center',
          }}>
            Attribute
          </Typography>

          <Box sx={{
            textAlign: 'center', position: 'relative', py: 0.5,
            borderRadius: 1, background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' },
              color: '#fff', letterSpacing: '-0.01em',
            }}>
              DubLabs
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.55rem',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)', mt: 0.2,
            }}>
              Recommended
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: { xs: '0.9rem', md: '1rem' },
              color: 'rgba(255,255,255,0.6)', letterSpacing: '-0.005em',
            }}>
              {competitorName}
            </Typography>
          </Box>
        </Box>

        {rows.map((row, i) => {
          const ourAdvantage = row.dublabs.win === 'us';
          const isTie = row.dublabs.win === 'tie';
          return (
            <Box
              key={row.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
                px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
                borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                alignItems: 'center',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
              }}
            >
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 600,
                fontSize: { xs: '0.78rem', md: '0.9rem' },
                color: '#fff', letterSpacing: '-0.005em', pr: 1,
              }}>
                {row.label}
              </Typography>

              <Box sx={{
                textAlign: 'center', px: { xs: 0.5, md: 1.5 }, py: 0.5,
                borderRadius: 1,
                background: ourAdvantage ? 'rgba(255,255,255,0.04)' : 'transparent',
              }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 500,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: '#fff', lineHeight: 1.4, letterSpacing: '-0.005em',
                }}>
                  {row.dublabs.text}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', px: { xs: 0.5, md: 1.5 } }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours={false} />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 400,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: isTie ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.4)',
                  lineHeight: 1.4, letterSpacing: '-0.005em',
                }}>
                  {row.competitor.text}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2 },
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)', alignItems: 'center',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: '0.7rem',
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
          }}>
            Wins
          </Typography>
          <Box sx={{
            textAlign: 'center', py: 0.75, borderRadius: 1,
            background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: '#fff', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'us').length}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: 'rgba(255,255,255,0.4)', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'them').length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

function AdvantageCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 2) * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.35s ease, background 0.35s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.24)',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 500, fontSize: '0.6rem',
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.35)', mb: 1.5,
        }}>
          0{index + 1}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: 'clamp(1.15rem, 1.8vw, 1.35rem)',
          color: '#fff', letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.6,
        }}>
          {item.body}
        </Typography>
      </Box>
    </motion.div>
  );
}

function UseCaseCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: item.highlight
          ? '1px solid rgba(255,255,255,0.28)'
          : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: item.highlight ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        boxShadow: item.highlight ? '0 0 40px rgba(255,255,255,0.04)' : 'none',
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: '1.15rem',
          color: '#fff', letterSpacing: '-0.02em', mb: 3, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.6 }}>
          {item.items.map((line, i) => (
            <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckIcon sx={{
                color: item.highlight ? '#fff' : 'rgba(255,255,255,0.4)',
                fontSize: 16, mt: 0.4, flexShrink: 0,
              }} />
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 400, fontSize: '0.88rem',
                color: 'rgba(255,255,255,0.6)', lineHeight: 1.5,
              }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function VsTraditionalDubbingPage() {
  const navigate = useNavigate();

  useSEO({
    title: 'DubLabs vs Traditional Dubbing — Minutes not Weeks',
    description:
      'DubLabs vs traditional dubbing studios: minutes not weeks, 90% cheaper, all languages in parallel, original speaker\'s voice preserved. Compare in detail.',
    canonical: 'https://dublabs.ai/vs/traditional-dubbing',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const goContact = () => navigate('/contact');

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh' }}>
      <Breadcrumbs items={BREADCRUMBS} />
      {/* Hero */}
      <Box sx={{
        position: 'relative',
        pt: { xs: 12, md: 18 },
        pb: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0) 55%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center', mb: 3,
            }}>
              Comparison · DubLabs vs Traditional Dubbing
            </Typography>

            <Typography component="h1" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              color: '#fff', lineHeight: 0.98, letterSpacing: '-0.05em',
              textAlign: 'center', mb: 3,
              maxWidth: 1000, mx: 'auto',
            }}>
              Weeks collapse<br />into minutes.
            </Typography>

            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.7vw, 1.25rem)',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.55, textAlign: 'center',
              maxWidth: 720, mx: 'auto', mb: 5,
            }}>
              Studios still deliver the best cinematic dubs for prestige film. But for teams shipping video
              weekly — creator, edtech, corporate, media — the studio calendar and budget are the bottleneck.
              DubLabs replaces the calendar with a dashboard.
            </Typography>

            <Box sx={{
              display: 'flex', flexWrap: 'wrap',
              justifyContent: 'center', gap: 1.2, mb: 6,
            }}>
              {['Minutes vs Weeks', '~90% Cost Cut', 'All Languages Parallel', 'Same Voice Every Language'].map((label) => (
                <Box key={label} sx={{
                  px: 2, py: 0.9,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(12px)',
                }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: '0.72rem',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.75)',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={goContact}
                sx={{
                  background: '#fff', color: '#000',
                  fontFamily: 'Inter', fontWeight: 700,
                  fontSize: '0.85rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 4, py: 1.6, borderRadius: '2px',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.88)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Comparison table */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Side-by-side"
            title="Where the differences matter"
            subtitle="A studio still wins where cinematic performance is the deliverable. Everywhere else, the economics don't survive contact with weekly production."
          />
          <ComparisonTable rows={COMPARISON_ROWS} competitorName="Traditional Studio" />
        </Container>
      </Box>

      {/* Where DubLabs wins */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Real Wedges"
            title="Where DubLabs wins"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {ADVANTAGES.map((adv, i) => (
              <AdvantageCard key={adv.title} item={adv} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Which is right */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Fit"
            title="Which is right for you?"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {USE_CASES.map((uc, i) => (
              <UseCaseCard key={uc.title} item={uc} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', mb: 2.5,
            }}>
              Skip the studio calendar
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              color: '#fff', lineHeight: 1.02,
              letterSpacing: '-0.045em', mb: 3,
            }}>
              Ship your first dub<br />before your next standup.
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.55, mb: 5,
              maxWidth: 560, mx: 'auto',
            }}>
              Book a demo, send us a clip, and we'll dub it in your target languages.
              Compare it against your studio quote — decide from there.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goContact}
              sx={{
                background: '#fff', color: '#000',
                fontFamily: 'Inter', fontWeight: 700,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 4, py: 1.6, borderRadius: '2px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.88)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Get in Touch
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
